/**
 * Audit 08.08.2026 — die Karte, die auf dem Dashboard gefehlt hat.
 *
 * Befund: Nach dem Umbau auf die Zustandsmaschine zeigte das Dashboard
 * weiterhin ausschließlich score-basierte Auswahl (TopPicksCard über
 * Conviction, ReversalRadarCard über den Boden-Score). Der einzige Signaltyp,
 * der out-of-sample geprüft ist und einen konkreten Kaufpreis nennt, tauchte
 * auf der Startseite überhaupt nicht auf — man musste ihn in der Watchlist
 * suchen.
 *
 * Diese Karte zeigt genau das und sonst nichts: welche Werte gerade eine
 * offene Kaufzone haben, zu welchem Limit, mit welchem Ungültigkeitspreis.
 * Plus die laufende Bilanz (§5b Kriterium 5: ohne Nenner ist eine Zahl nur
 * eine Behauptung).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target } from 'lucide-react';

import {
  fetchOffeneZonenSignale,
  fetchZonenBilanz,
  type OffenesZonenSignal,
  type ZonenBilanz,
} from '@/lib/v6/zone-signals-read';
import { CARD, CARD_HINT, EMPTY_STATE } from '@/lib/design-tokens';
import { useDemoMode } from '@/contexts/DemoModeContext';

function alterInTagen(datum: string): number {
  return Math.max(0, Math.round((Date.now() - Date.parse(datum)) / 86_400_000));
}

export function OffeneKaufzonenCard() {
  const [signale, setSignale] = useState<OffenesZonenSignal[] | null>(null);
  const [bilanz, setBilanz] = useState<ZonenBilanz | null>(null);
  // Siehe HandlungslisteCard: DemoApp rendert das echte Dashboard, ein
  // direkter Pfad wirft Demo-Besucher auf die Loginseite.
  const { linkTo } = useDemoMode();

  useEffect(() => {
    const ctrl = new AbortController();
    void fetchOffeneZonenSignale(ctrl.signal).then(setSignale);
    void fetchZonenBilanz(ctrl.signal).then(setBilanz);
    return () => ctrl.abort();
  }, []);

  return (
    <div className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <Target className="size-4 text-emerald-400" />
          Offene Kaufzonen
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">
          gemessen · antizyklisch
        </span>
      </div>
      <p className={`${CARD_HINT} mb-3`}>
        12–40 % unter dem Hoch, plus frische MA50-Rückeroberung oder
        Bollinger-Squeeze mit relativer Stärke. Rund 10 Signale im Jahr, stark
        gehäuft nach Markteinbrüchen.{' '}
        <span className="text-zinc-500">
          Neumessung 08.08.: gegenüber „an einem beliebigen Tag kaufen" ist
          <em> kein</em> Vorteil nachweisbar (−0,02 R, Bereich −0,24 bis +0,20).
          Der Vorteil zeigt sich nur im Zeitraum, aus dem die Schwellen stammen.
          Was bleibt, ist festes Risiko und eine feste Regel — Disziplin, kein
          gemessener Vorsprung.
        </span>
      </p>

      {signale === null && <div className="skeleton h-16 w-full" />}

      {signale !== null && signale.length === 0 && (
        <div className={EMPTY_STATE}>
          Aktuell keine offene Kaufzone. Das ist der Normalzustand — bei ~20
          Signalen im Jahr ist an den meisten Tagen nichts zu tun.
        </div>
      )}

      {signale !== null && signale.length > 0 && (
        <ul className="space-y-2">
          {signale.map((s) => (
            <li key={s.id}>
              <Link
                to={linkTo(`/symbol/swing/${encodeURIComponent(s.symbol)}`)}
                className="block rounded border border-emerald-800/50 bg-emerald-950/20 px-2.5 py-2 hover:bg-emerald-950/40 transition-colors focus-ring"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-sm font-semibold text-zinc-100">
                    {s.symbol}
                  </span>
                  <span className="text-[10px] text-zinc-500">
                    {s.ausloeser} · Tag {alterInTagen(s.generated_date) + 1}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-emerald-200 font-mono tabular-nums">
                  Limit bis {s.entry_price.toFixed(2)}
                  <span className="text-zinc-500"> · ungültig unter </span>
                  {s.stop_price.toFixed(2)}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {bilanz && bilanz.gesamt > 0 && (
        <div className="mt-3 border-t border-zinc-800/60 pt-2 text-[10px] text-zinc-400 leading-snug">
          Bisherige Bilanz: {bilanz.gesamt} aufgelöst · {bilanz.ausgestoppt} ausgestoppt ·{' '}
          {bilanz.zeitausstieg} nach 26 Wochen verkauft
          {bilanz.schnitt_pct !== null && (
            <> · Ø {bilanz.schnitt_pct >= 0 ? '+' : ''}{bilanz.schnitt_pct.toFixed(1)} %</>
          )}
          {bilanz.schnitt_r !== null && <> ({bilanz.schnitt_r.toFixed(2)} R)</>}
        </div>
      )}
      {bilanz === null && signale !== null && (
        <div className="mt-3 border-t border-zinc-800/60 pt-2 text-[10px] text-zinc-500 leading-snug">
          Noch keine abgeschlossenen Signale — die Live-Bilanz beginnt mit dem
          ersten Abschluss. Die Rückrechnung über 22 Jahre steht im
          Signal-Konzept §7.
        </div>
      )}
    </div>
  );
}
